import type { Market } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';

export interface MarketErrorTarget {
  field: 'countries' | 'currency' | 'root.server';
  key: string;
  values?: Record<string, unknown>;
}

/**
 * Where a market save failure lands when the backend sent no usable `details`: 422 with
 * `meta.countries` on the countries field (naming the clashing countries), INVALID_REFERENCE
 * on the price list and any 409 (deactivating the default) on the form itself.
 */
export function marketErrorTarget(error: unknown): MarketErrorTarget | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === 'EXCHANGE_RATE_MISSING') {
    return {
      field: 'currency',
      key: 'markets:errors.rateMissing',
      values: { from: error.meta.baseCurrency, to: error.meta.quoteCurrency },
    };
  }
  if (Array.isArray(error.meta.countries)) {
    const countries = error.meta.countries.filter((c): c is string => typeof c === 'string');
    return { field: 'countries', key: 'markets:errors.countriesTaken', values: { countries } };
  }
  if (error.code === 'INVALID_REFERENCE')
    return { field: 'root.server', key: 'markets:errors.priceList' };
  if (error.kind === 'conflict')
    return { field: 'root.server', key: 'markets:errors.defaultLocked' };
  return null;
}

/** Deleting or deactivating the default market answers 409; a currency without a rate 422. */
export function marketChangeErrorKey(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === 'EXCHANGE_RATE_MISSING') return 'markets:errors.rateMissingShort';
  return error.kind === 'conflict' ? 'markets:errors.defaultLocked' : null;
}

/** Countries already served by another market of the store; the picker greys them out. */
export function takenCountries(markets: readonly Market[], exceptId?: string): Map<string, string> {
  const taken = new Map<string, string>();
  for (const market of markets) {
    if (market.id === exceptId) continue;
    for (const code of market.countries) taken.set(code, market.name);
  }
  return taken;
}
